import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../ui/Button';

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const [prevLocation, setPrevLocation] = useState(location);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu when route changes
    if (prevLocation !== location) {
        setPrevLocation(location);
        setIsOpen(false);
    }

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'Services', path: '/services' },
        { name: 'Portfolio', path: '/portfolio' },
        { name: 'Blog', path: '/blog' },
        { name: 'About', path: '/about' },
    ];

    return (
        <motion.nav
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
            className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-background/80 backdrop-blur-md shadow-sm border-b border-slate-200/50 py-4' : 'bg-transparent py-6'}`}
        >
            <div className="container mx-auto px-6 flex justify-between items-center">
                <Link to="/" className="flex items-center gap-2.5 text-xl md:text-2xl font-bold tracking-tighter text-slate-900 shrink-0">
                    <picture>
                        <source srcSet="/logo.webp" type="image/webp" />
                        <img src="/logo.png" alt="Acodr Solutions Logo" width="40" height="40" fetchpriority="high" className="h-8 md:h-10 w-auto object-contain" />
                    </picture>
                    <span className="flex">
                        {"Acodr".split("").map((letter, i) => (
                            <motion.span
                                key={i}
                                animate={{ y: [0, -3, 0] }}
                                transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                    delay: i * 0.08,
                                    ease: "easeInOut",
                                }}
                                className={letter === " " ? "w-1" : ""}
                            >
                                {letter}
                            </motion.span>
                        ))}
                        <motion.span
                            animate={{ y: [0, -3, 0] }}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                delay: 5 * 0.08,
                                ease: "easeInOut",
                            }}
                            className="text-primary"
                        >
                            .
                        </motion.span>
                    </span>
                </Link>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center space-x-8">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            className={`transition-colors text-sm font-medium ${location.pathname === link.path
                                ? 'text-primary'
                                : 'text-slate-600 hover:text-primary'
                                }`}
                        >
                            {link.name}
                        </Link>
                    ))}
                    <Link to="/contact">
                        <Button variant="glow" className="px-5 py-2 text-sm">
                            Contact
                        </Button>
                    </Link>
                </div>

                {/* Mobile Menu Button */}
                <div className="md:hidden">
                    <button onClick={() => setIsOpen(!isOpen)} className="text-slate-900" aria-label="Toggle navigation menu">
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden glass border-t border-slate-200 overflow-hidden"
                    >
                        <div className="flex flex-col p-6 space-y-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className={`transition-colors text-lg ${location.pathname === link.path
                                        ? 'text-primary'
                                        : 'text-slate-600 hover:text-primary'
                                        }`}
                                >
                                    {link.name}
                                </Link>
                            ))}
                            <Link to="/contact">
                                <Button variant="glow" className="w-full">
                                    Contact Us
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.nav>
    );
};

export default Navbar;